import { useEffect, useMemo, useRef, useState } from 'react';
import '../../styles/hero/heroStage.css';
import { projectsList } from '../../data/projects';
import { captureEvent } from '../../lib/analytics';
import Link from '../Link';
import { projectPath } from '../../data/projects';
import type { DeviceScene } from '../../lib/deviceScene';
import { hasHardwareWebGL } from '../../lib/capabilities';

/* Dark and light sites alternate, so the stage never settles into one colour. */
const ORDER = [
    'acom-detailing',
    'roos-heylen',
    'nerv-models',
    'bosmans-studentenkamers',
    'studiebooster',
    'tuinen-tuur-geerits',
];

/* A recording runs 10s; if its end is never reported (autoplay blocked), move on anyway. */
const FALLBACK_MS = 13000;

interface HeroStageProps {
    /** Start loading and playing (after the loader has lifted). */
    play: boolean;
    /** The project now on show: the hero takes on its colours and its light or dark mood. */
    onActive?: (projectId: string) => void;
}

type Mode = 'none' | 'compact' | 'full';

/* No 3D on a connection that is slow or that the visitor asked to keep light (Save-Data), nor without a GPU. */
function lightConnection(): boolean {
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
    return Boolean(connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType ?? '')));
}

/* Decided at the first render, so the phone's room is reserved from the first paint and nothing shifts later. */
function currentMode(): Mode {
    if (lightConnection() || !hasHardwareWebGL()) return 'none';
    if (window.matchMedia('(min-width: 768px)').matches) return 'full';
    return window.matchMedia('(max-width: 767px) and (min-height: 680px)').matches ? 'compact' : 'none';
}

/*
 * The work, on devices: a real 3D MacBook and iPhone whose screens play recordings of the live client
 * sites, each scrolling down its page with the site's own animations, then moving on to the next
 * project. The scene (three.js, two compressed models, twelve short videos) loads after the page has
 * settled. Wide screens get the laptop and the phone and they lean toward the cursor; a tall phone
 * gets the iPhone alone, between the labels and the headline, once the page has loaded.
 */
const HeroStage = ({ play, onActive }: HeroStageProps) => {
    const root = useRef<HTMLDivElement>(null);
    const canvas = useRef<HTMLCanvasElement>(null);
    const scene = useRef<DeviceScene | null>(null);
    const projects = useMemo(
        () => ORDER.map((id) => projectsList.find((project) => project.id === id)).filter((p): p is NonNullable<typeof p> => Boolean(p)),
        []
    );
    const [active, setActive] = useState(0);
    const [mode, setMode] = useState<Mode>(currentMode);
    const shown = mode !== 'none';
    const [ready, setReady] = useState(false);
    const count = projects.length;
    const activeRef = useRef(active);
    activeRef.current = active;
    const reducedMotion = useMemo(() => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches, []);

    // Wide screens get the full stage, a tall phone gets the phone alone; a short phone, or a light connection,
    // keeps the flowing background and the headline.
    useEffect(() => {
        const wide = window.matchMedia('(min-width: 768px)');
        const tall = window.matchMedia('(max-width: 767px) and (min-height: 680px)');
        const update = () => setMode(currentMode());
        wide.addEventListener('change', update);
        tall.addEventListener('change', update);
        return () => {
            wide.removeEventListener('change', update);
            tall.removeEventListener('change', update);
        };
    }, []);

    // Build the scene once the page has settled.
    useEffect(() => {
        if (!play || !shown || !canvas.current) return;
        let cancelled = false;
        let created: DeviceScene | null = null;
        const element = canvas.current;
        const phoneOnly = mode === 'compact';
        let start = 0;
        // On a phone the page is the priority: wait for it to finish loading, then a moment more.
        const begin = () => {
            start = window.setTimeout(launch, phoneOnly ? 1200 : 350);
        };
        const launch = () => {
            import('../../lib/deviceScene')
                .then(({ createDeviceScene }) =>
                    createDeviceScene({
                        canvas: element,
                        reducedMotion,
                        phoneOnly,
                        onReady: () => undefined,
                        onEnded: (id) => {
                            if (cancelled || reducedMotion || projects[activeRef.current]?.id !== id) return;
                            setActive((index) => (index + 1) % count);
                        },
                    })
                )
                .then((instance) => {
                    if (cancelled) instance.dispose();
                    else {
                        created = instance;
                        scene.current = instance;
                        setReady(true); // the effect below puts the first project on show
                    }
                })
                .catch(() => undefined); // no WebGL: the hero keeps its background and headline
        };
        if (phoneOnly && document.readyState !== 'complete') window.addEventListener('load', begin, { once: true });
        else begin();
        return () => {
            cancelled = true;
            window.removeEventListener('load', begin);
            window.clearTimeout(start);
            created?.dispose();
            scene.current = null;
            setReady(false);
        };
    }, [play, shown, mode, count, projects, reducedMotion]);

    // A new project: send it to the screens, start fetching the one after it, and set a fallback.
    useEffect(() => {
        const instance = scene.current;
        if (!ready || !instance) return;
        instance.show(projects[active].id);
        instance.preload(projects[(active + 1) % count].id);
        if (reducedMotion) return;
        const timer = window.setTimeout(() => setActive((index) => (index + 1) % count), FALLBACK_MS);
        return () => window.clearTimeout(timer);
    }, [ready, active, count, projects, reducedMotion]);

    const current = projects[active];
    const currentId = current.id;
    useEffect(() => {
        if (shown) onActive?.(currentId);
    }, [currentId, shown, onActive]);

    return (
        <div ref={root} className="stage" data-ready={ready} data-mode={mode}>
            <canvas ref={canvas} className="stage__canvas" aria-hidden="true" />
            <Link
                className="stage__caption label"
                to={projectPath(current.id)}
                aria-label={`Bekijk project: ${current.name}`}
                onClick={() => captureEvent('hero_project_clicked', { project_name: current.name, project_url: current.link })}
            >
                {String(active + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}
                <span>{current.name} ↗</span>
            </Link>
        </div>
    );
};

export default HeroStage;
